<template>
  <v-container fluid class="my-5">
    <div class="d-flex justify-space-between align-center flex-wrap mb-4">
      <div>
        <h2 class="subheading grey--text">Horarios y Accesos de empleados</h2>
        <p class="grey--text text--darken-1 mb-0">Define los días y horarios en que cada empleado puede acceder al fraccionamiento.</p>
      </div>
      <v-btn color="primary" @click="openDialog()">
        <v-icon left>mdi-plus</v-icon>
        Nuevo horario
      </v-btn>
    </div>

    <v-card class="elevation-2">
      <v-data-table
        :headers="headers"
        :items="scheduleItems"
        :loading="loading"
        :no-data-text="'No hay horarios de empleados registrados'"
      >
        <template v-slot:item.employee_name="{ item }">
          <strong>{{ item.employee_name }}</strong>
        </template>
        <template v-slot:item.days_display="{ item }">
          <v-chip v-for="day in item.days_display" :key="day" small class="mr-1 mb-1">{{ day }}</v-chip>
        </template>
        <template v-slot:item.schedule="{ item }">{{ item.entry_time }} - {{ item.exit_time }}</template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-3" v-on="on" @click="viewAccessLog(item)">mdi-history</v-icon>
            </template>
            <span>Ver registros de acceso</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-3" v-on="on" @click="editSchedule(item)">mdi-pencil</v-icon>
            </template>
            <span>Editar horario</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on" @click="deleteSchedule(item)">mdi-delete-outline</v-icon>
            </template>
            <span>Eliminar horario</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent max-width="620">
      <v-form @submit.prevent="saveSchedule()">
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title>{{ editing ? 'Editar horario' : 'Nuevo horario' }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-5">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="schedule.employee"
                  :items="employeeOptions"
                  item-text="text"
                  item-value="value"
                  label="Empleado"
                  outlined
                  required
                  :disabled="editing"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <div class="caption grey--text text--darken-1 mb-2">Días con acceso autorizado</div>
                <v-btn-toggle v-model="selectedDays" multiple class="d-flex flex-wrap">
                  <v-btn
                    v-for="(day, index) in weekDaysOptions"
                    :key="index"
                    small
                    :value="index"
                  >
                    {{ day }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="schedule.entry_time"
                  label="Hora de entrada"
                  type="time"
                  outlined
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="schedule.exit_time"
                  label="Hora de salida"
                  type="time"
                  outlined
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn text color="primary" @click="dialog = false" :disabled="loading">Cancelar</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" type="submit" :loading="loading" :disabled="loading">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <v-dialog v-model="accessLogDialog" max-width="900">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Registros de acceso{{ selectedSchedule ? `: ${selectedSchedule.employee_name}` : '' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="accessLogDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-data-table
          :headers="accessLogHeaders"
          :items="accessLogs"
          :loading="loadingAccessLogs"
          :no-data-text="'No hay registros de acceso para este empleado'"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50], itemsPerPageText: 'Filas por página' }"
        ></v-data-table>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import employeeService, { extractApiErrorMessage } from '@/services/employeeService'

export default {
  name: 'oficina_empleados',
  data () {
    return {
      employees: [],
      schedules: [],
      dialog: false,
      accessLogDialog: false,
      editing: false,
      editingScheduleId: null,
      selectedSchedule: null,
      accessLogs: [],
      loading: false,
      loadingAccessLogs: false,
      weekDaysOptions: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
      selectedDays: [],
      schedule: this.emptySchedule(),
      headers: [
        { text: 'Empleado', value: 'employee_name' },
        { text: 'Días autorizados', value: 'days_display', sortable: false },
        { text: 'Horario', value: 'schedule', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      accessLogHeaders: [
        { text: 'Fecha', value: 'date' },
        { text: 'Entrada', value: 'entry_time' },
        { text: 'Salida', value: 'exit_time' }
      ]
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    housingUnitId () {
      return this.getProfile.idHousingUnit
    },
    employeeOptions () {
      return this.employees.map(emp => {
        const userInfo = (emp.user || emp.user_detail) || {}
        return {
          text: `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim(),
          value: emp.id
        }
      })
    },
    scheduleItems () {
      return this.schedules.map(schedule => {
        const employee = this.employees.find(e => e.id === schedule.employee)
        const userInfo = (employee && (employee.user || employee.user_detail)) || {}
        return {
          id: schedule.id,
          employee: schedule.employee,
          employee_name: `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim(),
          entry_time: schedule.entry_time,
          exit_time: schedule.exit_time,
          days_display: schedule.days_display || [],
          days_of_week: schedule.days_of_week,
          raw: schedule
        }
      })
    }
  },
  mounted () {
    this.loadData()
  },
  watch: {
    housingUnitId (newVal) {
      if (newVal) {
        this.loadData()
      }
    }
  },
  methods: {
    async loadData () {
      if (!this.housingUnitId) {
        console.error('Housing unit ID not available')
        return
      }
      this.loading = true
      try {
        const [employeesData, schedulesData] = await Promise.all([
          employeeService.getEmployees(this.housingUnitId),
          employeeService.getEmployeeSchedules(this.housingUnitId)
        ])
        this.employees = employeesData.results || employeesData
        this.schedules = schedulesData.results || schedulesData
      } catch (error) {
        console.error('Error loading data:', error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', extractApiErrorMessage(error))
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    emptySchedule () {
      return {
        employee: '',
        entry_time: '08:00',
        exit_time: '17:00',
        days_of_week: ''
      }
    },
    daysStringToArray (daysString) {
      if (!daysString) return []
      return daysString.split('').map(d => parseInt(d))
    },
    daysArrayToString (daysArray) {
      if (!daysArray || !daysArray.length) return ''
      return daysArray.sort().join('')
    },
    openDialog () {
      this.editing = false
      this.editingScheduleId = null
      this.schedule = this.emptySchedule()
      this.selectedDays = []
      this.dialog = true
    },
    editSchedule (item) {
      this.editing = true
      this.editingScheduleId = item.id
      this.selectedDays = this.daysStringToArray(item.days_of_week)
      this.schedule = {
        employee: item.employee,
        entry_time: item.entry_time,
        exit_time: item.exit_time,
        days_of_week: item.days_of_week
      }
      this.dialog = true
    },
    async viewAccessLog (item) {
      this.selectedSchedule = item
      this.accessLogs = []
      this.loadingAccessLogs = true
      try {
        const response = await employeeService.getEmployeeAccessLogs(item.employee)
        this.accessLogs = response.results || response
      } catch (error) {
        console.error('Error loading access logs:', error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', extractApiErrorMessage(error))
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loadingAccessLogs = false
      }
      this.accessLogDialog = true
    },
    async saveSchedule () {
      if (!this.schedule.employee || !this.selectedDays.length || !this.schedule.entry_time || !this.schedule.exit_time) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'Completa todos los campos requeridos')
        this.$store.commit('snackbar_activate_true')
        return
      }

      if (this.schedule.entry_time >= this.schedule.exit_time) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'La hora de entrada debe ser menor a la de salida')
        this.$store.commit('snackbar_activate_true')
        return
      }

      try {
        this.loading = true
        const scheduleData = {
          employee: this.schedule.employee,
          entry_time: this.schedule.entry_time,
          exit_time: this.schedule.exit_time,
          days_of_week: this.daysArrayToString(this.selectedDays)
        }

        if (this.editing) {
          await employeeService.updateEmployeeSchedule(this.editingScheduleId, scheduleData)
          this.$store.commit('snackbar_change_message', 'Horario actualizado correctamente')
        } else {
          await employeeService.createEmployeeSchedule(scheduleData)
          this.$store.commit('snackbar_change_message', 'Horario creado correctamente')
        }

        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.dialog = false
        await this.loadData()
      } catch (error) {
        console.error('Error saving schedule:', error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', extractApiErrorMessage(error))
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    async deleteSchedule (item) {
      if (!confirm('¿Estás seguro de que deseas eliminar este horario?')) return

      try {
        this.loading = true
        await employeeService.deleteEmployeeSchedule(item.id)
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Horario eliminado correctamente')
        this.$store.commit('snackbar_activate_true')
        await this.loadData()
      } catch (error) {
        console.error('Error deleting schedule:', error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', extractApiErrorMessage(error))
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
